import { lazy, Suspense } from 'react';
import { Navigate } from 'react-router-dom';
import ProtectedRoute from '@/app/router/ProtectedRoute';
import { LoadingState } from '@/shared/components/LoadingState';
import { useLanguage } from '@/core/i18n/LanguageContext';
import LoginPage from '@/modules/auth/screens/LoginPage';
import AuthCallbackPage from '@/modules/auth/screens/AuthCallbackPage';

function RouteSuspenseFallback() {
  const { t } = useLanguage();
  return <LoadingState message={t('loading')} />;
}

/**
 * Heavy screens load when their route renders. Login stays in the first bundle.
 */
function lazyDefault(loader) {
  const Page = lazy(loader);
  return (
    <Suspense fallback={<RouteSuspenseFallback />}>
      <Page />
    </Suspense>
  );
}

function lazyNamed(loader, exportName) {
  const Page = lazy(() => loader().then((mod) => ({ default: mod[exportName] })));
  return (
    <Suspense fallback={<RouteSuspenseFallback />}>
      <Page />
    </Suspense>
  );
}

function guarded(node, permissions) {
  if (!permissions) {
    return <ProtectedRoute>{node}</ProtectedRoute>;
  }
  return <ProtectedRoute requiredPermissions={permissions}>{node}</ProtectedRoute>;
}

/**
 * Route manifest: path, optional requiredRoles, and element.
 * Order matters for overlapping paths (Chint meter before generic meter).
 */
export const APP_ROUTES = [
  { path: '/login', element: <LoginPage />, public: true },
  { path: '/auth/callback', element: <AuthCallbackPage />, public: true },
  { path: '/digital-twin', element: guarded(lazyDefault(() => import('@/modules/dashboard/screens/SolarDigitalTwinPage'))) },
  {
    path: '/dashboard',
    element: guarded(lazyDefault(() => import('@/modules/dashboard/screens/DashboardPage'))),
  },
  {
    path: '/analysis',
    element: guarded(lazyDefault(() => import('@/modules/analysis/screens/AnalysisPage'))),
  },
  {
    path: '/production-planning',
    element: guarded(
      lazyDefault(() => import('@/modules/planning/screens/PlanningPage')),
      ['energy:read'],
    ),
  },
  {
    path: '/control',
    element: guarded(
      lazyDefault(() => import('@/modules/control/screens/ControlPage')),
      ['commands:request'],
    ),
  },
  {
    path: '/users',
    element: guarded(
      lazyDefault(() => import('@/modules/users/screens/UsersPage')),
      ['users:read'],
    ),
  },
  {
    path: '/companies',
    element: guarded(
      lazyDefault(() => import('@/modules/companies/screens/CompaniesPage')),
      ['companies:read'],
    ),
  },
  {
    path: '/devices',
    element: guarded(lazyDefault(() => import('@/modules/devices/screens/DevicesPage'))),
  },
  {
    path: '/devices/:id',
    element: guarded(lazyDefault(() => import('@/modules/devices/screens/DataLoggerDetailPage'))),
  },
  {
    path: '/devices/:id/inverter/:inverterId',
    element: guarded(lazyDefault(() => import('@/modules/devices/screens/InverterDetailPage'))),
  },
  {
    path: '/devices/:id/inverter/:inverterId/parameters',
    element: guarded(lazyDefault(() => import('@/modules/devices/screens/DetailedParametersPage'))),
  },
  {
    // Legacy /chint bookmarks → same unified Energy Meter detail page.
    path: '/devices/:deviceId/energy-meter/:meterId/chint',
    element: guarded(lazyDefault(() => import('@/modules/devices/screens/EnergyMeterDetailPage'))),
  },
  {
    path: '/devices/:deviceId/energy-meter/:meterId',
    element: guarded(lazyDefault(() => import('@/modules/devices/screens/EnergyMeterDetailPage'))),
  },
  {
    path: '/alarms',
    element: guarded(lazyDefault(() => import('@/modules/alarms/screens/AlarmsPage'))),
  },
  { path: '/kosova', element: <Navigate to="/monitoring" replace />, public: true },
  {
    path: '/monitoring',
    element: guarded(lazyDefault(() => import('@/modules/fleet/screens/MonitoringPage'))),
  },
  {
    path: '/reports',
    element: guarded(lazyDefault(() => import('@/modules/reports/screens/ReportsPage'))),
  },
  {
    path: '/dashboard/pv-fleet',
    element: guarded(lazyDefault(() => import('@/modules/fleet/screens/PvFleetEnergyDashboardPage'))),
  },
  {
    path: '/settings',
    element: guarded(lazyNamed(() => import('@/modules/account/screens/SettingsPage'), 'SystemSettingsPage')),
  },
  {
    path: '/me',
    element: guarded(lazyDefault(() => import('@/modules/account/screens/MePage'))),
  },
  {
    path: '/connections',
    element: guarded(lazyNamed(() => import('@/modules/account/screens/ConnectionsPage'), 'ConnectionsPage')),
  },
  {
    path: '/about',
    element: guarded(lazyNamed(() => import('@/modules/account/screens/AboutPage'), 'AboutPage')),
  },
  {
    path: '/platform-lab',
    element: guarded(
      lazyDefault(() => import('@/modules/platform/screens/PlatformLabPage')),
      ['users:write'],
    ),
  },
  {
    path: '/',
    element: <Navigate to="/dashboard" replace />,
    public: true,
  },
];
